import { Alert, Badge, Group, Paper, Stack, Text, Title, Collapse, UnstyledButton } from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from '../hooks/useTranslation';
import { useState } from 'react';
import { useEquipmentStore } from '../stores/equipmentStore';
import { useDealStore } from '../stores/dealStore';
import { useConflicts } from '../hooks/useConflicts';
import { usePagination } from '../hooks/usePagination';
import { PaginationControls } from './PaginationControls';
import type { Conflict } from '../types/conflict';

// Store pour gerer l'onglet actif
interface TabStore {
  activeTab: string | null;
  setActiveTab: (tab: string | null) => void;
}

import { create } from 'zustand';
export const useTabStore = create<TabStore>()((set) => ({
  activeTab: 'inventory',
  setActiveTab: (tab: string | null) => set(() => ({ activeTab: tab })),
}));

const getSeverityColor = (severity?: string) => {
  switch (severity) {
    case 'CRITIQUE':
      return 'red';
    case 'ELEVEE':
      return 'orange';
    case 'MOYENNE':
      return 'yellow';
    case 'FAIBLE':
      return 'blue';
    default:
      return 'gray';
  }
};

export function ConflictAlert() {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const setSelectedEquipment = useEquipmentStore(state => state.setSelectedEquipment);
  const setDealFilters = useDealStore(state => state.setFilters);
  const setActiveTab = useTabStore(state => state.setActiveTab);

  // Use pagination hook
  const { page, limit, setPage, setLimit } = usePagination({
    initialPage: 1,
    initialLimit: 20,
  });

  const { data: conflicts, total, isLoading } = useConflicts({ page, limit });

  if (isLoading || !conflicts?.length) {
    return null;
  }

  const handleConflictClick = (conflict: Conflict) => {
    if (conflict.type === 'stock' && conflict.equipmentId) {
      // Navigation vers l'onglet inventaire avec selection de l'equipement
      setSelectedEquipment(conflict.equipmentId);
      setActiveTab('inventory' as string | null);
    } else if (conflict.type === 'deal_end_date') {
      // Navigation vers l'onglet affaires avec les filtres appropries
      if (conflict.endDate) {
        // Affaire avec date de fin depassee
        setDealFilters({
          status: ['ACTIVE', 'RETURNING'],
          endDatePassed: true
        });
      } else {
        // Affaire sans date de fin
        setDealFilters({
          status: ['ACTIVE'],
          withoutEndDate: true
        });
      }
      setActiveTab('deals' as string | null);
    }
  };

  return (
    <Paper withBorder p="md">
      <Stack gap="sm">
        <UnstyledButton onClick={() => setIsExpanded(!isExpanded)}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              <IconAlertTriangle size={24} color="orange" />
              <Title order={3}>{t('conflicts.title')}</Title>
              {isExpanded ? <IconChevronDown size={20} /> : <IconChevronRight size={20} />}
            </Group>
            <Badge size="lg" variant="filled" color="orange">
              {total} {t('conflicts.detected')}
            </Badge>
          </Group>
        </UnstyledButton>

        <Collapse in={isExpanded}>
          <Stack gap="xs">
            {conflicts.map((conflict, index) => (
              <UnstyledButton
                key={index}
                onClick={() => handleConflictClick(conflict)}
                style={{ width: '100%' }}
              >
                <Alert
                  variant="light"
                  color={getSeverityColor(conflict.severity)}
                  title={conflict.type === 'stock' ? t('conflicts.stockConflict') :
                         conflict.endDate ? t('conflicts.dealEndDateConflict') :
                         t('conflicts.dealWithoutEndDateConflict')}
                >
                  <Group justify="space-between" align="flex-start">
                    <Stack gap="xs">
                      <Text size="sm">{conflict.message}</Text>
                      {conflict.startDate && (
                        <Text size="xs" c="dimmed">
                          {t('conflicts.startDate')}: {new Date(conflict.startDate).toLocaleDateString('fr-FR')}
                        </Text>
                      )}
                      {conflict.endDate && (
                        <Text size="xs" c="dimmed">
                          {t('conflicts.endDate')}: {new Date(conflict.endDate).toLocaleDateString('fr-FR')}
                        </Text>
                      )}
                    </Stack>
                    {conflict.daysCount && (
                      <Badge variant="dot" color={getSeverityColor(conflict.severity)}>
                        {conflict.daysCount} {t('conflicts.days')}
                      </Badge>
                    )}
                  </Group>
                </Alert>
              </UnstyledButton>
            ))}

            {/* Pagination Controls */}
            <PaginationControls
              page={page}
              limit={limit}
              total={total}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  );
}
